import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { errorMessage } from '../../lib/api';
import { parseMoney } from '../../lib/format';
import {
  addBusinessProduct,
  createBusiness,
  listAttendants,
  listPipelines,
  listStages,
  products as productsApi,
} from '../../lib/resources';
import type { Business, Lead } from '../../types';
import { Button, ErrorNote, Field, Modal, inputClass } from '../ui';

/**
 * "Criar negócio" a partir do lead (menu da linha e seção Negócios da ficha).
 * Desenho nosso (decisão Q23): pipeline/etapa, atendente, produto e valor.
 */
export function LeadBusinessModal({
  lead,
  onClose,
  onCreated,
}: {
  lead: Pick<Lead, 'id' | 'name'>;
  onClose: () => void;
  onCreated: (business: Business) => void;
}) {
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [attendantId, setAttendantId] = useState('');
  const [productId, setProductId] = useState('');
  const [value, setValue] = useState('');

  const pipelines = useQuery({ queryKey: ['pipelines'], queryFn: listPipelines });
  const stages = useQuery({
    queryKey: ['stages', pipelineId],
    queryFn: () => listStages(pipelineId),
    enabled: Boolean(pipelineId),
  });
  const attendants = useQuery({ queryKey: ['attendants'], queryFn: listAttendants });
  const products = useQuery({
    queryKey: ['products'],
    queryFn: () => productsApi.list({ take: 200 }),
  });

  const create = useMutation({
    mutationFn: async () => {
      const business = await createBusiness({
        leadId: lead.id,
        stageId,
        attendantId: attendantId || undefined,
        total: value && !productId ? parseMoney(value) : undefined,
      });
      if (productId) {
        return addBusinessProduct(business.id, {
          productId,
          price: value ? parseMoney(value) : undefined,
        });
      }
      return business;
    },
    onSuccess: onCreated,
  });

  return (
    <Modal title={`Criar negócio — ${lead.name}`} onClose={onClose}>
      <div className="space-y-3">
        <Field label="Pipeline *">
          <select
            className={inputClass}
            value={pipelineId}
            onChange={(event) => {
              setPipelineId(event.target.value);
              setStageId('');
            }}
          >
            <option value="">Selecione…</option>
            {(pipelines.data?.data ?? []).map((pipeline) => (
              <option key={pipeline.id} value={pipeline.id}>
                {pipeline.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Etapa *">
          <select
            className={inputClass}
            value={stageId}
            disabled={!pipelineId}
            onChange={(event) => setStageId(event.target.value)}
          >
            <option value="">Selecione…</option>
            {(stages.data ?? []).map((stage) => (
              <option key={stage.id} value={stage.id}>
                {stage.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Atendente">
          <select
            className={inputClass}
            value={attendantId}
            onChange={(event) => setAttendantId(event.target.value)}
          >
            <option value="">Sem atendente</option>
            {(attendants.data?.data ?? []).map((attendant) => (
              <option key={attendant.userId} value={attendant.userId}>
                {attendant.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Produto">
            <select
              className={inputClass}
              value={productId}
              onChange={(event) => setProductId(event.target.value)}
            >
              <option value="">Sem produto</option>
              {(products.data?.data ?? []).map((product) => (
                <option key={product.id} value={product.id}>
                  {product.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Valor"
            hint={productId ? 'Vazio = preço do produto' : 'Valor livre sem produto'}
          >
            <input
              className={inputClass}
              placeholder="R$ 0,00"
              value={value}
              onChange={(event) => setValue(event.target.value)}
            />
          </Field>
        </div>
        {create.error && (
          <ErrorNote>
            {errorMessage(create.error, 'Não foi possível criar o negócio')}
          </ErrorNote>
        )}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancelar</Button>
          <Button
            variant="primary"
            disabled={!stageId || create.isPending}
            onClick={() => create.mutate()}
          >
            {create.isPending ? 'Criando…' : 'Criar negócio'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
